import { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import "bootstrap/dist/css/bootstrap.css";
import "../../App/App.css";
import "../../assets/style.css";
import "../../assets/stencil_management.css";
import "../../assets/event_management.css";
import { useStencils } from "../../lib/hooks/useStencils";
import { useStencilWeeks } from "../../lib/hooks/useStencilWeeks";
import { useCategories } from "../../lib/hooks/useCategories";
import Search from "../components/Search/Search";
import Navbar from "../components/Navbar/Navbar.jsx";
import { useEvent } from "../../lib/hooks/useEvent";
import QuickUpdateModal from "../components/StencilManagement/QuickUpdate";
import { calculateProgress } from "../../lib/utils/progressBar";
import DisplayProgress from "../components/StencilManagement/DisplayProgress";
import { updateStencilWeek } from "../../lib/services/stencilWeekService.js";
import { adminStatus } from "../../lib/services/authService.js";
import FullScreenImg from "../components/StencilSelection/FullScreenImg";
import { sortStencils } from "../../lib/services/helperService.js";

const StencilManagement = ({
  isAdminAuthenticated,
  setIsAdminAuthenticated,
}) => {
  // Search filtering:
  const [query, setQuery] = useState("");
  const [allRows, setAllRows] = useState([]);

  const [selectedWeek, setSelectedWeek] = useState("all-weeks");
  const [selectedStep, setSelectedStep] = useState("all");
  const [selectedStatus, setSelectedStatus] = useState(null);
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [showQuickUpdate, setShowQuickUpdate] = useState(false);
  const [progress, setProgress] = useState({ completed: 0, total: 0 });

  const { stencils, getStencils } = useStencils();
  const { stencilWeeks, setStencilWeeks, getStencilWeeks } = useStencilWeeks();
  const { categories, getCategories } = useCategories();
  const { event, getEvent } = useEvent();

  const [filteredRows, setFilteredRows] = useState([]);

  const [isFullScreen, setIsFullScreen] = useState(false);
  const [selectedStencil, setSelectedStencil] = useState(null);

  async function refreshStencilWeeks() {
    await getStencilWeeks();
  }

  useEffect(() => {
    const checkAdminStatus = async () => {
      const response = await adminStatus();
      if (response.ok) {
        const data = await response.json();
        setIsAdminAuthenticated(data.isAuthenticated);
      }
    };

    checkAdminStatus();
  }, []);

  useEffect(() => {
    async function fetchData() {
      if (stencils?.length === 0) await getStencils();
      if (!event || Object.keys(event).length === 0) await getEvent();
      if (stencilWeeks?.length === 0) await getStencilWeeks();
      if (categories?.length === 0) await getCategories();
    }

    fetchData();
  }, []);

  useEffect(() => {
    const merged = stencilWeeks.map((week) => {
      const stencil = stencils.find((s) => s.id === week.stencil_id);
      return {
        id: stencil?.id || week.stencil_id,
        name: stencil?.name || "Unknown",
        category: stencil?.category_id || "Unknown",
        image_name: stencil?.image_name || undefined,
        weekNum: week.week_num,
        printed: week.printed,
        cut: week.cut,
        traced: week.traced,
        carved: week.carved,
      };
    });
    const sorted = sortStencils(merged);
    setAllRows(sorted);
    setFilteredRows(sorted);
  }, [stencils, stencilWeeks, event]);

  useEffect(() => {
    setSelectedCategories([]);
  }, [categories]);

  function getStepStatus(value) {
    switch (Number(value)) {
      case 0:
        return "not-started";
      case 1:
        return "in-progress";
      case 2:
        return "completed";
      case 3:
        return "confirmed";
      default:
        return null;
    }
  }

  function getStepStatusColor(value) {
    switch (Number(value)) {
      case 0:
        return "gray";
      case 1:
        return "yellow";
      case 2:
        return "blue";
      case 3:
        return "green";
      default:
        return "gray";
    }
  }

  function StepStatusCheckbox({ status, onClick }) {
    const colorClass = {
      gray: "status-gray",
      yellow: "status-yellow",
      blue: "status-blue",
      green: "status-green step-checked",
    }[status];

    return (
      <div
        className={`status-checkbox ${colorClass}`}
        onClick={onClick}
        style={{ cursor: "pointer" }}
      ></div>
    );
  }

  const toggleCategory = (category) => {
    if (selectedCategories.includes(category)) {
      let newCats = selectedCategories.filter((cat) => cat !== category);
      setSelectedCategories(newCats);
    } else setSelectedCategories([...selectedCategories, category]);
  };

  const handleStatusUpdate = (row, step) => {
    const steps = ["printed", "cut", "traced", "carved"];
    const stepIndex = steps.indexOf(step);

    const updatedRows = filteredRows.map((r) => {
      if (r.id === row.id && r.weekNum === row.weekNum) {
        const newStepStatuses = {};

        // Toggle step as not started/confirmed
        const isConfirming = r[step] === 0 || r[step] === 1 || r[step] === 2;

        for (let i = 0; i <= steps.length; i++) {
          const step = steps[i];
          if (isConfirming) {
            newStepStatuses[step] = i <= stepIndex ? 3 : r[step];
          } else {
            newStepStatuses[step] = i >= stepIndex ? 0 : r[step];
          }
        }

        try {
          const oldInd = stencilWeeks.findIndex(
            (sw) => sw.stencil_id === r.id && sw.week_num === r.weekNum
          );

          const oldStencilWeek = stencilWeeks[oldInd];
          const newStencilWeek = {
            ...oldStencilWeek,
            printed: Number(newStepStatuses.printed),
            cut: Number(newStepStatuses.cut),
            traced: Number(newStepStatuses.traced),
            carved: Number(newStepStatuses.carved),
          };

          // Optimistic update
          const response = updateStencilWeek(
            r.weekNum,
            r.id,
            newStepStatuses.printed,
            newStepStatuses.cut,
            newStepStatuses.traced,
            newStepStatuses.carved
          );
          const updatedSW = [...stencilWeeks];
          updatedSW[oldInd] = newStencilWeek;
          setStencilWeeks(updatedSW);

          // Revert on failure
          response.then((res) => {
            if (!res.ok) {
              // Revert stencilWeeks
              updatedSW[oldInd] = oldStencilWeek;
              setStencilWeeks(updatedSW);

              // Revert allRows
              const oldRowInd = allRows.findIndex(
                (row) =>
                  row.id === oldStencilWeek.stencil_id &&
                  row.weekNum === Number(oldStencilWeek.week_num)
              );

              if (oldRowInd) {
                // Replace relevant row in allRows back with the original row
                const oldRow = allRows[oldRowInd];
                const newRow = {
                  ...oldRow,
                  printed: oldStencilWeek.printed,
                  cut: oldStencilWeek.cut,
                  traced: oldStencilWeek.traced,
                  carved: oldStencilWeek.carved,
                };
                const newRows = [...allRows];
                newRows[oldRowInd] = newRow;
                setAllRows(newRows);

                // Revert filtered rows
                const newFilteredRows = [...filteredRows];
                newFilteredRows[oldRowInd] = newRow;
                setFilteredRows(newFilteredRows);
              }

              alert(
                `Error updating status for stencil ${r.id} week ${r.weekNum}`
              );
            }
          });
        } catch (err) {
          console.error(err);
        }
      }
      return r;
    });

    setAllRows(updatedRows);
  };

  useEffect(() => {
    if (allRows?.length) {
      const progress = calculateProgress(allRows, selectedWeek, selectedStep);
      const timeout = setTimeout(() => {
        setProgress(progress);
      }, 500);
      return () => clearTimeout(timeout);
    }
  }, [allRows, selectedWeek, selectedStep]);

  const refreshProgress = () => {
    const progress = calculateProgress(allRows, selectedWeek, selectedStep);
    const timeout = setTimeout(() => {
      setProgress(progress);
    }, 500);
    return () => clearTimeout(timeout);
  };

  const handleWeekClick = (week) => setSelectedWeek(week);
  const handleStepClick = (step) => setSelectedStep(step);
  const handleStatusClick = (status) => {
    if (selectedStatus === status) setSelectedStatus(null);
    else setSelectedStatus(status);
  };
  const handleQuickUpdate = () => setShowQuickUpdate(true);

  return (
    <>
      <Navbar
        isAdminAuthenticated={isAdminAuthenticated}
        setIsAdminAuthenticated={setIsAdminAuthenticated}
        location={"/admin/stencil-management"}
      />
      <div className="week-and-update-container" style={{ paddingTop: "80px" }}>
        <div className="week-buttons">
          {[...Array(event?.num_weeks)].map((_, index) => {
            const week = `week${index + 1}`;
            return (
              <button
                key={week}
                className={`week-button ${week} ${
                  selectedWeek === week ? "selected" : ""
                }`}
                id={week}
                onClick={() => handleWeekClick(week)}
              >
                Week {index + 1}
              </button>
            );
          })}
          <button
            className={`week-button all-weeks ${
              selectedWeek === "all-weeks" ? "selected" : ""
            }`}
            id="all-weeks"
            onClick={() => handleWeekClick("all-weeks")}
          >
            All
          </button>
        </div>

        <button className="quick-update-button" onClick={handleQuickUpdate}>
          Quick Update
        </button>
      </div>

      <div className="step-and-search-container">
        <div className="grouped-box">
          <div className="top-row">
            <div className="stencil-sidebar col">
              <div
                className="scrollable-lhs scroll-invis"
                style={{ paddingTop: "5px" }}
              >
                {categories?.map((category) => {
                  return (
                    <a
                      key={`category-${category.id}`}
                      id={category.id}
                      className={`${
                        selectedCategories.includes(category.id)
                          ? "stencil-sidebar-text-focused"
                          : "stencil-sidebar-text"
                      }`}
                      onClick={() => toggleCategory(category.id)}
                    >
                      {`${category.id} - ${category.name}`}
                    </a>
                  );
                })}
              </div>
            </div>
            {/* <div className="step-and-status-container"> */}
            <div className="step-buttons">
              <button
                className={`step-button all ${
                  selectedStep === "all" ? "selected" : ""
                }`}
                id="all"
                onClick={() => handleStepClick("all")}
              >
                All
              </button>

              <button
                className={`step-button printed ${
                  selectedStep === "printed" ? "selected" : ""
                }`}
                id="printed"
                onClick={() => handleStepClick("printed")}
              >
                Printed
              </button>

              <button
                className={`step-button cut ${
                  selectedStep === "cut" ? "selected" : ""
                }`}
                id="cut"
                onClick={() => handleStepClick("cut")}
              >
                Cut
              </button>

              <button
                className={`step-button traced ${
                  selectedStep === "traced" ? "selected" : ""
                }`}
                id="traced"
                onClick={() => handleStepClick("traced")}
              >
                Traced
              </button>

              <button
                className={`step-button carved ${
                  selectedStep === "carved" ? "selected" : ""
                }`}
                id="carved"
                onClick={() => handleStepClick("carved")}
              >
                Carved
              </button>
            </div>
          </div>

          <div className="status-heading">
            <h3>Status:</h3>
          </div>

          <div className="status-buttons">
            <button
              className={`status-button not-started ${
                selectedStatus === "not-started" ? "selected" : ""
              }`}
              id="not-started"
              onClick={() => handleStatusClick("not-started")}
            >
              Not Started
            </button>

            <button
              className={`status-button in-progress ${
                selectedStatus === "in-progress" ? "selected" : ""
              }`}
              id="in-progress"
              onClick={() => handleStatusClick("in-progress")}
            >
              In Progress
            </button>

            <button
              className={`status-button completed ${
                selectedStatus === "completed" ? "selected" : ""
              }`}
              id="completed"
              onClick={() => handleStatusClick("completed")}
            >
              Completed
            </button>

            <button
              className={`status-button confirmed ${
                selectedStatus === "confirmed" ? "selected" : ""
              }`}
              id="confirmed"
              onClick={() => handleStatusClick("confirmed")}
            >
              Confirmed
            </button>
          </div>
        </div>

        <div className="search-and-table-container">
          <div className="search-and-progress-container">
            <div className="search-bar">
              <Search
                query={query}
                setQuery={setQuery}
                range={allRows}
                setProduct={setFilteredRows}
              />
            </div>
            <div>
              <DisplayProgress
                completed={progress.completed}
                total={progress.total}
              />
            </div>
          </div>

          <div className="stencil-table">
            <table>
              <thead>
                <tr>
                  <th>SID</th>
                  <th>Title</th>
                  {selectedWeek === "all-weeks" && <th>Week</th>}
                  {selectedStep === "all" && (
                    <>
                      <th>Printed</th>
                      <th>Cut</th>
                      <th>Traced</th>
                      <th>Carved</th>
                    </>
                  )}
                  {selectedStep === "printed" && (
                    <>
                      <th>Printed</th>
                    </>
                  )}
                  {selectedStep === "cut" && (
                    <>
                      <th>Cut</th>
                    </>
                  )}
                  {selectedStep === "traced" && (
                    <>
                      <th>Traced</th>
                    </>
                  )}
                  {selectedStep === "carved" && (
                    <>
                      <th>Carved</th>
                    </>
                  )}
                </tr>
              </thead>

              <tbody>
                {filteredRows
                  .filter(
                    (r) =>
                      selectedWeek === "all-weeks" ||
                      `week${r.weekNum}` === selectedWeek
                  )
                  .filter((r) => {
                    if (!selectedStatus) return true;
                    if (selectedStep === "all") {
                      return getStepStatus(r.carved) === selectedStatus;
                    } else {
                      return getStepStatus(r[selectedStep]) === selectedStatus;
                    }
                  })
                  .filter((r) => {
                    return (
                      selectedCategories.length === 0 ||
                      selectedCategories.includes(r.category)
                    );
                  })
                  .map((r) => (
                    <tr
                      key={`${r.id}-${r.weekNum}`}
                      className={`tr-${r.weekNum}`}
                    >
                      <td>
                        <a
                          href="#"
                          onClick={(e) => {
                            e.preventDefault();
                            setSelectedStencil(r);
                            setIsFullScreen(true);
                          }}
                        >
                          {r.id}
                        </a>
                      </td>
                      <td>{r.name}</td>
                      {selectedWeek === "all-weeks" && <td>{r.weekNum}</td>}

                      {["all", "printed"].includes(selectedStep) && (
                        <td>
                          <StepStatusCheckbox
                            status={getStepStatusColor(r.printed)}
                            onClick={() => handleStatusUpdate(r, "printed")}
                          />
                        </td>
                      )}
                      {["all", "cut"].includes(selectedStep) && (
                        <td>
                          <StepStatusCheckbox
                            status={getStepStatusColor(r.cut)}
                            onClick={() => handleStatusUpdate(r, "cut")}
                          />
                        </td>
                      )}
                      {["all", "traced"].includes(selectedStep) && (
                        <td>
                          <StepStatusCheckbox
                            status={getStepStatusColor(r.traced)}
                            onClick={() => handleStatusUpdate(r, "traced")}
                          />
                        </td>
                      )}
                      {["all", "carved"].includes(selectedStep) && (
                        <td>
                          <StepStatusCheckbox
                            status={getStepStatusColor(r.carved)}
                            onClick={() => handleStatusUpdate(r, "carved")}
                          />
                        </td>
                      )}
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {!!isFullScreen && selectedStencil?.image_name && (
        <FullScreenImg
          imgSrc={`/api/stencils/files/${selectedStencil.image_name}`}
          onClose={() => setIsFullScreen(false)}
        />
      )}

      {showQuickUpdate && (
        <QuickUpdateModal
          onClose={() => {
            setShowQuickUpdate(false);
            refreshStencilWeeks();
          }}
          refreshProgress={refreshProgress}
          refreshStencilWeeks={refreshStencilWeeks}
        />
      )}
    </>
  );
};

export default StencilManagement;
